import type { Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  ListDeliveryAvailabilityDocument,
  ListDeliveryAvailabilityQuery,
  ListDeliveryAvailabilityQueryVariables,
} from "@/generated/graphql";
import { WEEKS } from "../constants";
import type { DeliveryAvailabilityData } from "../types";

/**
 * 配達依頼登録開始画面の配達便表示に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterStartDeliveryParams {
  deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  areaSelectValues: Ref<{ area_code: string; area_name: string }>;
  deliveryAvailabilityStore: Ref<DeliveryAvailabilityData[][]>;
  getAvailableDate: Ref<string>;
  btnDisabled: Ref<boolean>;
}

/**
 * 配達依頼登録開始画面の配達便表示を扱う composable。
 */
export const useDeliveryRequestRegisterStartDelivery = ({
  deliveryAvailabilityData,
  deliveryAvailabilityHeader,
  areaSelectValues,
  deliveryAvailabilityStore,
  getAvailableDate,
  btnDisabled,
}: UseDeliveryRequestRegisterStartDeliveryParams) => {
  // YYYY-MM-DD を M/D(曜) 形式へ変換する
  const formatDate = (date: string): string => {
    let _date;
    let retDate = "";
    if (date) {
      _date = new Date(date);
      retDate =
        _date.getMonth() +
        1 +
        "/" +
        _date.getDate() +
        "(" +
        WEEKS[_date.getDay()] +
        ")";
    }
    return retDate;
  };

  // 選択エリアの配達便一覧を取得し、日付ごとの表示データへ整形する
  const showDeliveryAvailability = async (
    area_code: string | null
  ): Promise<void> => {
    const apolloClient = await generateApolloClient();
    deliveryAvailabilityData.value = [];
    deliveryAvailabilityHeader.value = new Map();

    const result: ApolloQueryResult<ListDeliveryAvailabilityQuery> =
      await apolloClient.query<
        ListDeliveryAvailabilityQuery,
        ListDeliveryAvailabilityQueryVariables
      >({
        query: ListDeliveryAvailabilityDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
          area_code: area_code ? area_code : "",
        },
        fetchPolicy: "no-cache",
      });

    const list = result.data.listDeliveryAvailability;
    if (!list) {
      return;
    }

    // API結果は複数エリア分を含むため、先頭エリアのみを表示対象として構築する
    let defaultAreaCode = "";
    let defaultAreaName = "";
    let currentDate = "";
    let currentAvialibity: DeliveryAvailabilityData[] = [];
    let idx = 0;

    list.forEach((value) => {
      if (value) {
        if (defaultAreaCode.length == 0) {
          defaultAreaCode = value.area_code;
          defaultAreaName = value.area_name;
        }
        if (value.area_code !== defaultAreaCode) {
          return;
        }
        value.delivery_date = value.delivery_date.substring(0, 10);
        if (currentDate.length == 0 || currentDate != value.delivery_date) {
          currentDate = value.delivery_date;
          if (currentAvialibity.length > 0) {
            deliveryAvailabilityData.value.push(currentAvialibity);
          }
          currentAvialibity = [];
          idx = 0;
        }
        currentAvialibity.push({
          key: ++idx,
          delivery_id: value.delivery_id,
          delivery_id_desc: value.delivery_id_desc,
          delivery_date: value.delivery_date,
          availability: value.availability.trim(),
          area_code: value.area_code,
          delivery_start_time: value.delivery_start_time,
          delivery_end_time: value.delivery_end_time,
        });
        deliveryAvailabilityHeader.value.set(
          value.delivery_id_desc,
          value.delivery_id_desc
        );
      }
    });

    if (currentAvialibity.length > 0) {
      deliveryAvailabilityData.value.push(currentAvialibity);
    }
    // 表示に使ったエリアをセレクトの選択値にも反映する
    if (defaultAreaCode !== "") {
      areaSelectValues.value = {
        area_code: defaultAreaCode,
        area_name: defaultAreaName,
      };
    }
  };

  // 定期ポーリング用: 全配達便を取得してストア連携用データを更新する
  const getDeliveryAvailable = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();
    const resultAvailability: ApolloQueryResult<ListDeliveryAvailabilityQuery> =
      await apolloClient.query<
        ListDeliveryAvailabilityQuery,
        ListDeliveryAvailabilityQueryVariables
      >({
        query: ListDeliveryAvailabilityDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
        },
        fetchPolicy: "no-cache",
      });
    const list = resultAvailability.data.listDeliveryAvailability;
    if (!list) {
      return;
    }

    // 「配達便データ取得日」を保持し、次画面で整合性チェックに利用する
    const _date = new Date();
    getAvailableDate.value = _date.getDate().toString();
    let currentDate = "";
    const allAvialibity: DeliveryAvailabilityData[] = [];
    let idx = 0;

    list.forEach((value) => {
      if (value) {
        value.delivery_date = value.delivery_date.substring(0, 10);
        if (currentDate.length == 0 || currentDate != value.delivery_date) {
          currentDate = value.delivery_date;
          idx = 0;
        }
        allAvialibity.push({
          key: ++idx,
          delivery_id: value.delivery_id,
          delivery_id_desc: value.delivery_id_desc,
          delivery_date: value.delivery_date,
          availability: value.availability.trim(),
          area_code: value.area_code,
          delivery_start_time: value.delivery_start_time,
          delivery_end_time: value.delivery_end_time,
        });
      }
    });

    deliveryAvailabilityStore.value = [];
    deliveryAvailabilityStore.value.push(allAvialibity);
    // 初期データ取得完了後に画面操作を解放する
    btnDisabled.value = false;
  };

  return {
    formatDate,
    showDeliveryAvailability,
    getDeliveryAvailable,
  };
};
